/**
 * THE DRAWER'S STATE SHAPE — a LEAF module, on purpose.
 *
 * `FilterState` and its defaults used to live in ./FilterContext, which
 * imports `dynamicFilterSlice` for its actions while that slice imports the
 * defaults back — a real import CYCLE that worked only because the store is
 * always pulled in first, so the slice finished evaluating before anything
 * reached the context. Import the context from a module that loads earlier
 * (the dashboard's widget dialog does, through reportWidgets/paneSeed) and
 * the slice reads `defaultFilterState` mid-initialisation:
 *   Uncaught ReferenceError: Cannot access 'defaultFilterState' before initialization
 * — a blank screen, in a file neither side touched.
 *
 * The shape has no dependencies of its own, so it sits here with NO imports
 * and both sides read it from below. FilterContext re-exports both names, so
 * every existing `from ".../FilterContext"` import is unchanged.
 */

export interface FilterState {
  search: string;
  source: string;
  sourceType: string;
  metaPageId: string;
  metaAdAccountId: string;
  /** Comma-separated campaign ids; options from /campaigns?isActive=true */
  campaignId: string;
  role: string;
  user: string;
  assignmentStatus: string;
  /** Comma-separated branch ids (primary lead branch); options from scoped /core/branches */
  branchId: string;
  /** Admin-only: only leads with no active branch assignment. Disables branchId. */
  noBranch: boolean;
  /** Lead priority (one of: high|medium|low|none). Empty string = All / no filter. */
  priority: string;
  /** Lead gender (one of: Male|Female|Other). Empty string = All / no filter. */
  gender: string;
  /** Comma-separated country dial codes (e.g. "+91,+971"); matches Leads.phoneCountryCode. */
  phoneCountryCode: string;
  stage: string;
  status: string;
  subStatus: string;
  inactiveFor: number;
  /** Aligned idle filter (days) — the panel's "Inactive for" field writes
   *  this (report/dashboard idle semantics). See leadFilterSlice. */
  idleOpenFor: number;
  /** Which activity kinds `idleOpenFor` counts — comma list of server tokens
   *  (updates|followups|notes|calls). "" = ALL kinds (the report widget's
   *  definition). See leadFilterSlice. */
  idleTouches: string;
  lastContactedStartDate: string;
  lastContactedEndDate: string;
  contactedBy: string;
  nextFollowupDate: string;
  /** Active follow-up date range (matches Pending followups in this window). */
  followupStartDate: string;
  followupEndDate: string;
  /** Pending TRACK follow-ups due in this window (separate from lead follow-ups). */
  trackFollowupStartDate: string;
  trackFollowupEndDate: string;
  /** Comma list of track ids the track follow-up window is narrowed to. */
  trackFollowupTrackId: string;
  lastActivityDate: string;
  /**
   * ACTOR-ACTIVITY filter — leads that HAD this kind of team activity in the
   * range (assigned / called / note / chat / follow-up / …), optionally by
   * specific people. Same params the reports activity explorer's ↗ pushes;
   * resolved server-side by `lead.activity-filter.ts`.
   */
  activityType: string;
  activityBy: string;
  activityStart: string;
  activityEnd: string;
  /**
   * CALL filters. Everything here narrows the SAME call log row — direction +
   * outcome + caller + window + talk time are AND-ed inside one relation
   * clause, so "outgoing, missed, by Sajitha, in August" means one call that
   * was all four.
   *
   * `callActivity` flips that from "has such a call" to "has none":
   *   ""      → off
   *   "has"   → at least one matching call
   *   "none"  → no matching call (bare = never called at all)
   */
  callActivity: string;
  /** Comma list of `in` | `out`. Empty / both = any resolved direction. */
  callDirection: string;
  /** Comma list of `connected` | `missed` | `no_answer` | `rejected`. */
  callOutcome: string;
  /** Comma list of `MOBILE_APP` | `TELECMI` (CallLogs.category). */
  callCategory: string;
  /** User id whose line the call was on. Calls only — unlike `contactedBy`,
   *  which also matches WhatsApp. */
  callBy: string;
  /** Target plan line whose counted leads are listed (`targetLineId`), for
   *  `targetUserId` (empty = the viewer), as of `targetAsOf` (empty = today). */
  targetLineId: string;
  targetUserId: string;
  targetAsOf: string;
  callStartDate: string;
  callEndDate: string;
  /** Optional IST wall-clock bounds ("HH:mm") on the call window — same
   *  semantics as the main date filter's startTime/endTime. */
  callStartTime: string;
  callEndTime: string;
  /** TALK-TIME seconds. Implies connected server-side: on an unanswered call
   *  `duration` is ring time, so a bare range over it would lie. */
  minCallDuration: number;
  maxCallDuration: number;
  /** How many calls matched the rest of this section. Leads with zero matching
   *  calls never match (that's `callActivity: "none"`). */
  minCallCount: number;
  maxCallCount: number;
  hasCallRecording: boolean;
  hasChoices: boolean;
  numberOfChoices: number;
  choicesAppliedIn: string;
  choicesLastUpdated: string;
  startDate: string;
  endDate: string;
  /**
   * Optional IST wall-clock times narrowing the date range, 24-hour "HH:mm"
   * ("" = whole day). They are the endpoints of ONE continuous window, not a
   * per-day time slice: 29 Jul 14:00 → 31 Jul 17:00 includes all of the 30th.
   * Serialized to `?startTime=` / `?endTime=`.
   */
  startTime: string;
  endTime: string;
  dateType: string;
  /**
   * When `dateType === "statusChangedAt"`, WHICH stages the change was INTO —
   * a comma list of stage ids, matched as a union ("entered Qualify OR
   * Application in the window", server: `buildStageEnteredBetween`). `""` =
   * "Any stage change" (the blanket last-stageChange behaviour). On a dynamic
   * stage tab this is defaulted to that tab's stage server-side. Serialized to
   * `?stageChangedToStageId=<id>[,<id>…]`.
   *
   * Written from two places, both of which ASK first: the Date Filters "Into
   * stage" picker, and the prompt the Stage filter raises on selection
   * (`StageDateBasisDialog`).
   */
  stageChangedTo: string;
  /**
   * ENTRY COUNTS LEADS THAT MOVED ON. Under "Status Changed At" with a stage
   * target, the count is normally "entered the stage in the range AND is
   * still there" — so today's intake shrinks as the team works it. With this
   * on, a lead that entered the stage inside the range counts wherever it
   * sits now (owner, 2026-09-25: "stage changed to Eligible current day, even
   * if it goes to another stage, it should be in the count"). Serialized to
   * `?entryMovedOn=true`; a superset of the strict reading, never smaller.
   */
  entryMovedOn: boolean;
  /**
   * "Status Changed At" companion — the INTAKE (first) stage's id, so the
   * server puts that stage's bucket on `createdAt` (leads are CREATED into
   * it; a stage change is not an event that happened to them) and every other
   * stage on the blanket change window. This makes the blanket list equal the
   * Segregation Explorer's "Moved in range" row exactly; the Date Type picker
   * and the report ↗ both set it. `""` = not sent (pure blanket, the legacy
   * reading). Serialized to `?intakeStageId=`.
   */
  intakeStageId: string;
  /**
   * "Status Changed At" ENTRY LEVEL — which pipeline level's change the date
   * range counts. `""` = stage moves (the default every report ↗ relies on:
   * "entered the stage in the window", then a Status chip narrows to who
   * sits in that status NOW). `"status"` = the range runs on
   * `statusChangedAt` (the lead / one deal changed INTO a selected status in
   * the window); `"subStatus"` = on `subStatusChangedAt`. This is how
   * "Offer Issued TODAY" is asked — a lead that entered Offer Letter last
   * month has no stage move today. Only meaningful under
   * dateType="statusChangedAt" with the matching Status / Sub Status chips;
   * the serializer drops it otherwise. Serialized to `?entryLevel=`.
   *
   * Asked, never assumed: the Stage / Status / Sub Status prompt
   * (`StageDateBasisDialog`) offers it whenever statuses are selected.
   */
  entryLevel: string;
  /**
   * `"lead"` = the Stage / Status / Sub-status filters match the LEAD's own
   * row only — no child-aware swap (`applyChildAwareStageFilter` skips). Set
   * by report drill ↗s, whose cells bucket every lead by its own stage; the
   * child-aware default surfaced a lead under Qualify through its DEAL while
   * the cell counted it under Pre Visa (6 vs 7). `""` = default child-aware.
   * Cleared whenever the stage selection is edited by hand. Serialized to
   * `?stageGrain=lead`.
   */
  stageGrain: string;
  /**
   * `"lead"` = the Campaign filter matches the lead's OWN campaign only. Set
   * by the campaign report's drill ↗: its rows put every lead in exactly one
   * campaign (the one on the lead itself), while the default filter also
   * lists leads that merely RE-ENQUIRED through the campaign — the row said
   * 503 and its list opened 546. `""` = default (own campaign OR a re-enquiry
   * through it). Cleared whenever the campaign selection is edited by hand.
   * Serialized to `?campaignGrain=lead`.
   */
  campaignGrain: string;
  /**
   * Meta AD SET / AD (the creative) — comma lists of MetaAdset / MetaAd row
   * ids, matched against the lead's OWN first-touch ad set / ad (the ad its
   * original submission came through), the grain the reports' ad rows count.
   * Picked in the drawer's Source & Campaign section (MetaAdFilter) or set
   * by the report ↗s (campaign breakdown, Ad Performance card). Shown as
   * their own removable chips, named from GET /core/meta-ads/options.
   * `""` = none. Serialized to `?adsetId=` / `?adId=`.
   */
  adsetId: string;
  adId: string;
  /**
   * The report bar's INTAKE pill, set by a report drill ↗ whose number was
   * computed with it: `"<year>-<month0>"` keys (month 0-indexed), comma-joined
   * — the bar's own value, verbatim. The server runs the reports' own intake
   * scope on the leads list, so a cell narrowed by the pill opens a list of
   * the same size (it used to open the un-narrowed one: the leads page had no
   * param for it). NOT the drawer's Intake control — that is the year × month
   * pair in `customFieldPairs`. Shown as its own removable "Intake" chip.
   * `""` = none. Serialized to `?intakeKeys=`.
   */
  intakeKeys: string;
  /**
   * Follow-up board strip ↗ from the dashboard (JSON: strip, bucket and the
   * card's request). Pins the list to exactly the leads the strip counted.
   * `""` = none. Serialized to `?followUpLoad=`; shown as its own chip.
   */
  followUpLoad: string;
  /**
   * Archived-lead policy. `""` = page default (archived hidden); `"all"` =
   * include archived. Set by report drill ↗s whose widget counts archived
   * leads (reports count archived everywhere, 2026-08-11) so the opened list
   * is the same population as the number clicked. Serialized to `?archived=`.
   */
  archived: string;
  isRevoked: boolean;
  isReenquired: boolean | "all"; // "all" = every re-enquiry (the toolbar button); true = badge-carrying only
  isActive: string;
  /** Active anchor filter (old-CRM "uniquify" port): the selected anchor's
   *  stage id, or null when off. Conditions live on that isAnchorPoint stage. */
  verifiedAnchor: number | null;
  revokeType: "all" | "noAccess" | "branchChange" | "reassigned";
  trackId: string;
  subTrackId: string;
  trackStatus: string;
  trackStartDate: string;
  trackEndDate: string;
  hasActiveTrack: boolean;
  // Payment filters (apply at the child / deal level)
  paymentStatus: string;
  hasDealValue: boolean;
  hasPendingAmount: boolean;
  hasInstallmentPlan: boolean;
  hasOverdueInstallments: boolean;
  /** "1:paid,2:unpaid" — every rule on the same deal (server: lead.filter.ts installmentFilters). */
  instRules: string;
  /** "" | "overdue" | "today" | "week" */
  instDue: string;
  minDealValue: number;
  maxDealValue: number;
  minPendingAmount: number;
  maxPendingAmount: number;
  // Industry-specific niche filters (universityId, courseId, videoServiceId, etc.)
  nicheFilters: Record<string, string | number | boolean>;
  // Dynamic custom field filters: { [customInputId]: selectedOptionId }
  customFieldFilters: Record<string, string>;
  /**
   * CORRELATED custom-field selections — two fields that describe ONE thing and
   * must match on the same record. Keyed `"<fieldAId>_<fieldBId>"`, valued as a
   * comma list of `"<optionAId>-<optionBId>"` tokens; serialized to
   * `?cfPair_<a>_<b>=…`.
   *
   * Today this is Intake Year × Intake Month: independent filters would AND into
   * a cross product (years [2026,2027] × months [Dec,Jan] = four intakes), which
   * made "December 2026 or January 2027" impossible to express. See
   * `features/customField/utils/intakePair.ts`.
   */
  customFieldPairs: Record<string, string>;
}

export const defaultFilterState: FilterState = {
  search: "",
  source: "",
  sourceType: "ALL",
  metaPageId: "",
  metaAdAccountId: "",
  campaignId: "",
  role: "",
  user: "",
  assignmentStatus: "all",
  branchId: "",
  noBranch: false,
  priority: "",
  gender: "",
  phoneCountryCode: "",
  stage: "",
  status: "",
  subStatus: "",
  inactiveFor: 0,
  idleOpenFor: 0,
  idleTouches: "",
  lastContactedStartDate: "",
  lastContactedEndDate: "",
  contactedBy: "",
  nextFollowupDate: "",
  followupStartDate: "",
  followupEndDate: "",
  trackFollowupStartDate: "",
  trackFollowupEndDate: "",
  trackFollowupTrackId: "",
  lastActivityDate: "",
  activityType: "",
  activityBy: "",
  activityStart: "",
  activityEnd: "",
  callActivity: "",
  callDirection: "",
  callOutcome: "",
  callCategory: "",
  callBy: "",
  targetLineId: "",
  targetUserId: "",
  targetAsOf: "",
  callStartDate: "",
  callEndDate: "",
  callStartTime: "",
  callEndTime: "",
  minCallDuration: 0,
  maxCallDuration: 0,
  minCallCount: 0,
  maxCallCount: 0,
  hasCallRecording: false,
  hasChoices: false,
  numberOfChoices: 0,
  choicesAppliedIn: "",
  choicesLastUpdated: "",
  startDate: "",
  endDate: "",
  startTime: "",
  endTime: "",
  dateType: "createdAt",
  stageChangedTo: "",
  entryMovedOn: false,
  intakeStageId: "",
  entryLevel: "",
  stageGrain: "",
  campaignGrain: "",
  adsetId: "",
  adId: "",
  intakeKeys: "",
  followUpLoad: "",
  archived: "",
  isRevoked: false,
  isReenquired: false,
  isActive: "all",
  verifiedAnchor: null,
  revokeType: "all",
  trackId: "",
  subTrackId: "",
  trackStatus: "",
  trackStartDate: "",
  trackEndDate: "",
  hasActiveTrack: false,
  paymentStatus: "",
  hasDealValue: false,
  hasPendingAmount: false,
  hasInstallmentPlan: false,
  hasOverdueInstallments: false,
  instRules: "",
  instDue: "",
  minDealValue: 0,
  maxDealValue: 0,
  minPendingAmount: 0,
  maxPendingAmount: 0,
  nicheFilters: {},
  customFieldFilters: {},
  customFieldPairs: {},
};
